import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Select, Text } from '@mantine/core';
import { listShipMethods, shipMethodLabel } from './api';

interface Props {
  value: string | null;
  onChange: (shipMethodId: string | null) => void;
  label?: string;
}

/**
 * The carrier picker — one control, used by ship confirm and by a wave shipment.
 *
 * **It says when the list is empty rather than being an empty dropdown.** Carriers are not seeded,
 * so a tenant who has not set any up would otherwise get a control that opens onto nothing and
 * says nothing — which is exactly why 069 left the picker out until there was a master behind it.
 * The description points at the screen that fixes it.
 *
 * **Active only.** A deactivated carrier stays on the shipments that already name it and leaves
 * the pickers; that is the whole difference between deactivating and deleting.
 *
 * Clearable, because no carrier is a legitimate answer. Legacy's ShipVia is nullable and a
 * warehouse without a carrier account still ships.
 */
export function CarrierSelect({ value, onChange, label }: Props) {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['ship-methods', 'active'] as const,
    queryFn: ({ signal }) => listShipMethods(false, signal),
  });

  const data = (query.data ?? []).map((m) => ({
    value: m.shipMethodId,
    label: shipMethodLabel(m),
  }));

  const empty = query.isSuccess && data.length === 0;

  return (
    <Select
      label={label ?? t('picks.carrier')}
      placeholder={empty ? t('picks.carrierNone') : t('picks.carrierPlaceholder')}
      description={
        empty ? <Text component="span" size="xs">{t('picks.carrierEmptyHint')}</Text> : undefined
      }
      data={data}
      value={value}
      onChange={onChange}
      disabled={query.isLoading || empty}
      searchable
      clearable
      nothingFoundMessage={t('shell.noMatch')}
    />
  );
}
